/** One page of a server-paged list, with the total the filters match. */
export interface ListPage<T> {
  results: T[]
  totalCount: number
  page: number
  pageSize: number
}

/** The order a server-paged list is sorted in — an API field and a direction. */
export interface ListSort {
  orderBy: string
  orderByDirection: 'ASCENDING' | 'DESCENDING'
}

/**
 * Order parameters for a list request: the sort picked in the list, or the
 * list's own default order while nothing is picked. A picked sort keeps the
 * blanks at the bottom whichever way it runs.
 */
export function sortParams(
  sort: ListSort | null | undefined,
  defaults: Record<string, string>
): Record<string, string> {
  return sort ? { ...sort, nullsOrderPolicy: 'LAST' } : defaults
}

/** The most rows the API's list endpoints return in one page. */
export const MAX_PAGE_SIZE = 100

/** How many full pages a whole-list walk reads before it stops. */
const MAX_PAGES = 25

/**
 * The rows a whole-list walk read, and whether it stopped at its page limit
 * before the end of the list. The total is there when the endpoint reports it.
 */
export interface AllPages<T> {
  results: T[]
  truncated: boolean
  totalCount?: number
}

/**
 * Every row a paged list endpoint matches, for downloads and pickers — a list
 * on screen should ask for the page it shows. The first page usually reports
 * the total, in which case the pages after it are fetched at once; without a
 * total they are walked one at a time until a page comes back short.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number, pageSize: number) => Promise<{ results: T[]; totalCount?: number }>
): Promise<T[]> {
  return (await walkAllPages(fetchPage)).results
}

/** {@link fetchAllPages}, saying too whether the list ran past the page limit. */
export async function walkAllPages<T>(
  fetchPage: (page: number, pageSize: number) => Promise<{ results: T[]; totalCount?: number }>
): Promise<AllPages<T>> {
  const first = await fetchPage(1, MAX_PAGE_SIZE)
  const totalCount = first.totalCount
  if (first.results.length < MAX_PAGE_SIZE) {
    return { results: first.results, truncated: false, totalCount }
  }

  if (totalCount !== undefined) {
    const neededPages = Math.ceil(totalCount / MAX_PAGE_SIZE)
    const pageCount = Math.min(neededPages, MAX_PAGES)
    const rest = await Promise.all(
      Array.from({ length: pageCount - 1 }, (_, index) => fetchPage(index + 2, MAX_PAGE_SIZE))
    )
    const results = [...first.results, ...rest.flatMap((page) => page.results)]
    return { results, truncated: neededPages > MAX_PAGES, totalCount }
  }

  const results = [...first.results]
  for (let page = 2; page <= MAX_PAGES; page++) {
    const next = await fetchPage(page, MAX_PAGE_SIZE)
    results.push(...next.results)
    if (next.results.length < MAX_PAGE_SIZE) return { results, truncated: false }
  }
  // The last page allowed came back full, so the list may run on past it.
  return { results, truncated: true }
}
